import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Scissors, Clock, Loader2 } from 'lucide-react';
import { useService } from '@/contexts/ServiceContext';
import { Service } from '@/types';

interface ServiceSelectionProps {
  onSelectService: (service: Service) => void;
  /** Notifies the parent once active services are loaded (used to restore the last selection) */
  onServicesLoaded?: (services: Service[]) => void;
}

export function ServiceSelection({ onSelectService, onServicesLoaded }: ServiceSelectionProps) {
  const { t } = useTranslation();
  const { services, loading, listServices } = useService();

  useEffect(() => {
    // Load all active services
    listServices();
  }, []);

  const activeServices = useMemo(() => services?.filter((s) => s.isActive) || [], [services]);

  // Notify parent when active services are available (restore last selection)
  useEffect(() => {
    if (!loading && activeServices.length > 0 && onServicesLoaded) {
      onServicesLoaded(activeServices);
    }
  }, [loading, activeServices, onServicesLoaded]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-ink-subtle animate-spin" aria-hidden="true" />
      </div>
    );
  }

  if (activeServices.length === 0) {
    return (
      <div className="text-center py-12">
        <Scissors className="w-8 h-8 text-ink-subtle mx-auto mb-3" aria-hidden="true" />
        <p className="text-ink-muted">
          {t('booking.serviceSelection.empty')}
        </p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="font-sans text-xl font-semibold tracking-tight text-ink mb-1 flex items-center justify-center gap-2">
        <Scissors className="w-5 h-5 shrink-0 text-ink-subtle" aria-hidden="true" />
        {t('booking.serviceSelection.title')}
      </h2>
      <p className="text-sm text-ink-muted text-center mb-6">
        {t('booking.serviceSelection.subtitle')}
      </p>

      {/* Compact grid (2 cols on phones, up to 4 on large screens) so several
          services fit in one view, but each card keeps a big, clearly visible
          image band on top — easier to recognise for elderly users. */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
        {activeServices.map((service) => (
          <button
            key={service.serviceId}
            onClick={() => onSelectService(service)}
            className="group flex flex-col bg-surface rounded-xl border border-hairline hover:border-accent/60 transition-colors text-left overflow-hidden"
          >
            {/* Prominent image band */}
            <div className="h-28 sm:h-32 w-full bg-surface-3 flex items-center justify-center overflow-hidden">
              {service.imageUrl ? (
                <img
                  src={service.imageUrl}
                  alt=""
                  aria-hidden="true"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <Scissors className="w-8 h-8 text-ink-subtle" aria-hidden="true" />
              )}
            </div>

            {/* Service info */}
            <div className="flex flex-col flex-1 p-3">
              <h3 className="font-sans text-base sm:text-lg font-semibold text-ink leading-snug line-clamp-2">
                {service.name}
              </h3>

              {/* Duration & Price pinned to the bottom */}
              <div className="mt-auto pt-2 flex items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1.5 text-sm text-ink-muted">
                  <Clock className="w-3.5 h-3.5 text-ink-subtle" aria-hidden="true" />
                  {service.duration} {t('booking.serviceSelection.minutes')}
                </span>
                <span className="text-base font-semibold text-ink tabular-nums whitespace-nowrap">
                  €{(service.price / 100).toFixed(2)}
                </span>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
